import React from "react";
import styled, { css } from "styled-components";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import MopedIcon from "@mui/icons-material/Moped";
import HomeIcon from "@mui/icons-material/Home";
import VerifiedIcon from "@mui/icons-material/Verified";
import ReceiptIcon from "@mui/icons-material/Receipt";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import WifiIcon from "@mui/icons-material/Wifi";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import playStoreBadge from "../assets/img/Play Store.png";
import appStoreBadge from "../assets/img/App store Icon.png";
import { QRCodeSVG } from "qrcode.react";
import { formatCurrency } from "../config/market";
import { APP_STORE_URL, PLAY_STORE_URL } from "../config/appStores";
import { getOptimizedUrl } from "../utils/cloudinaryOptimize";
import { readVendorOpenState } from "../utils/vendorOpenState";
import { vendorTaglineOf } from "../utils/vendorTagline";
import { ORDER_AHEAD_LABEL } from "../utils/deliverySchedule";
import {
  dishesOf,
  restaurantHeroOf,
  tidyCuisine,
  tidyName,
} from "../utils/restaurantListing";

/**
 * "Download the App" on the landing page: three phones showing the customer
 * app's own screens.
 * ─────────────────────────────────────────────────────────────────────────────
 * The phones are drawn from what the page has ALREADY fetched, never from
 * invented copy. The old mockup showed "Krustys" and "Pizza Place", an "Open"
 * pill nobody computed and an "ETA: 15 mins" the backend has never published.
 *
 *   Restaurants  the landing page's own restaurant rail (promoted, verified,
 *                active), open kitchens first. The pill is the server's
 *                `openStatus` read through readVendorOpenState, so an unknown
 *                state draws nothing, exactly as on /restaurants.
 *   Order        the customer app's DoneScreen at "On the way": its four rungs,
 *                its wording, its colours. The one line item is the first
 *                deal, so the order is a real product at its real price from
 *                a named vendor.
 *   Deals        the DEALS & OFFERS rail above, at the same Cloudinary URL, so
 *                the browser already holds every photo.
 *
 * Nothing here is an asset, so nothing needs replacing when the line-up
 * changes. Restaurants are fetched on every landing visit and deals on every
 * full page load (Redux is not persisted). A newly promoted and verified
 * restaurant, a new photo (a new Cloudinary URL on the record) or a new deal
 * shows up on the next load; one that is unpromoted, deactivated, switched
 * off or unverified drops out, and the next in line takes its place.
 *
 * Until the data arrives (or if a rail is empty) each phone shows its own
 * skeleton rather than a made-up name. The whole stage is decorative: it is
 * hidden from assistive technology and its images carry empty alt text.
 *
 * SCALING. Each screen is laid out at the app's own size (360 x 760 pt, the
 * sizes in DoneScreen's StyleSheet) and scaled down as one picture, so the
 * proportions match the app at every breakpoint and only the scale changes.
 */

const SCREEN_W = 360;
const SCREEN_H = 760;
const BEZEL = 10;
const FRAME_W = SCREEN_W + BEZEL * 2;
const FRAME_H = SCREEN_H + BEZEL * 2;

/* Colours the customer app wears (Lusuku_Expo_Clean/app/config/colors.js and
   DoneScreen's STEP_ORANGE), so the phones look like the app they advertise. */
const APP = {
  appColor: "#324D54",
  light: "#f8f4f4",
  green: "#208103",
  stepOrange: "#fe7013",
  primary: "#fc5c65",
  dark: "#0c0c0c",
  darkGrey: "#04192E",
  medium: "#6e6969",
  hairline: "rgba(12,12,12,0.08)",
};

const SHOWCASE_RESTAURANTS = 3;
const SHOWCASE_DEALS = 6;

/* The pill a restaurant card may carry. null draws nothing: a missing field
   is not evidence that a kitchen is trading. */
const openPillOf = (openState) => {
  if (openState.isOpen === true) return { label: "Open", tone: "open" };
  if (openState.scheduledOrdersOnly)
    return { label: ORDER_AHEAD_LABEL, tone: "ahead" };
  if (openState.isOpen === false) return { label: "Closed", tone: "closed" };
  return null;
};

/* Open kitchens first: the phone shows what somebody could order right now. */
const openRank = ({ isOpen, scheduledOrdersOnly }) => {
  if (isOpen === true) return 0;
  if (scheduledOrdersOnly) return 1;
  if (isOpen === null) return 2;
  return 3;
};

const showcaseRestaurantsOf = (restaurants) =>
  (restaurants || [])
    .filter((row) => row && !row.isOutlet && row.name)
    .map((row) => ({ row, openState: readVendorOpenState(row) }))
    .sort((a, b) => openRank(a.openState) - openRank(b.openState))
    .slice(0, SHOWCASE_RESTAURANTS)
    .map(({ row, openState }) => {
      const hero = restaurantHeroOf(row, dishesOf(row));
      return {
        id: row._id,
        name: tidyName(row.name),
        // The app's card shows the restaurant's own tagline when it has one.
        tagline: vendorTaglineOf(row),
        cuisine: tidyCuisine(row.ownerDetails?.typeOfGoods),
        photo: hero ? getOptimizedUrl(hero.image, "appShowcaseDish") : null,
        // The raw `imageUrl`, as the landing page's restaurant rail paints
        // it, so the logo comes out of the browser cache.
        logo: row.imageUrl || null,
        pill: openPillOf(openState),
        closed: openState.isOpen === false && !openState.scheduledOrdersOnly,
      };
    });

const showcaseDealsOf = (deals) =>
  (deals || [])
    .filter((deal) => deal && deal.product_image && Number(deal.price) > 0)
    .slice(0, SHOWCASE_DEALS)
    .map((deal) => ({
      id: deal._id,
      title: tidyName(deal.title),
      price: Number(deal.price),
      vendorName: tidyName(deal.vendorName),
      // Same preset as the DEALS & OFFERS cards: same URL, already cached.
      image: getOptimizedUrl(deal.product_image, "productPad"),
    }));

/* DoneScreen's ladder at IN_TRANSIT: two rungs done, "On the way" current. */
const ORDER_RUNGS = [
  { key: "placed", title: "Order placed", Icon: CheckIcon },
  { key: "preparing", title: "Being prepared", Icon: CheckIcon },
  { key: "onTheWay", title: "On the way", Icon: MopedIcon },
  { key: "delivered", title: "Delivered", Icon: HomeIcon },
];
const ORDER_CURRENT_RUNG = 2;

const PhoneStatusBar = () => (
  <StatusBar>
    <span>9:41</span>
    <StatusIcons>
      <SignalCellularAltIcon />
      <WifiIcon />
      <BatteryFullIcon style={{ transform: "rotate(90deg)" }} />
    </StatusIcons>
  </StatusBar>
);

const Phone = ({ position, title, children, footer }) => (
  <PhoneSlot $position={position}>
    <PhoneFrame>
      <Screen>
        <Island />
        <TopBar>
          <PhoneStatusBar />
          <NavTitle>{title}</NavTitle>
        </TopBar>
        <ScreenBody>{children}</ScreenBody>
        {footer}
        <HomeIndicator />
      </Screen>
    </PhoneFrame>
  </PhoneSlot>
);

const RestaurantsScreen = ({ restaurants }) => (
  <>
    <SearchField>
      <SearchIcon />
      Search restaurants
    </SearchField>
    {restaurants.length
      ? restaurants.map((restaurant) => {
          const showLogoTile = Boolean(restaurant.photo && restaurant.logo);
          const heroSrc = restaurant.photo || restaurant.logo;
          return (
            <RestaurantCard key={restaurant.id}>
              <RestaurantHero $dim={restaurant.closed}>
                {heroSrc ? (
                  <img
                    src={heroSrc}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    data-fit={restaurant.photo ? "cover" : "contain"}
                  />
                ) : (
                  <RestaurantMenuIcon />
                )}
                {restaurant.pill && (
                  <OpenPill $tone={restaurant.pill.tone}>
                    {restaurant.pill.label}
                  </OpenPill>
                )}
              </RestaurantHero>
              <RestaurantBody $withLogo={showLogoTile}>
                {showLogoTile && (
                  <LogoTile>
                    <img
                      src={restaurant.logo}
                      alt=""
                      loading="lazy"
                      decoding="async"
                    />
                  </LogoTile>
                )}
                <RestaurantName>{restaurant.name}</RestaurantName>
                {restaurant.tagline && (
                  <RestaurantTagline>{restaurant.tagline}</RestaurantTagline>
                )}
                {restaurant.cuisine && (
                  <CuisineChip>{restaurant.cuisine}</CuisineChip>
                )}
              </RestaurantBody>
            </RestaurantCard>
          );
        })
      : Array.from({ length: SHOWCASE_RESTAURANTS }).map((_, index) => (
          <RestaurantCard key={index}>
            <RestaurantHero $skeleton />
            <RestaurantBody>
              <SkeletonLine $width="62%" $height={16} />
              <SkeletonLine $width="34%" $height={12} $gap={10} />
            </RestaurantBody>
          </RestaurantCard>
        ))}
  </>
);

const OrderScreen = ({ deal }) => (
  <>
    <OrderHero>
      <HeroBadge>
        <CheckIcon />
      </HeroBadge>
      <Headline>Order placed</Headline>
      <NextStep>Your rider is on the way to you.</NextStep>
      <LiveRow>
        <LiveDot />
        Live updates on
      </LiveRow>
    </OrderHero>

    <AppCard>
      {ORDER_RUNGS.map(({ key, title, Icon }, index) => {
        const done = index < ORDER_CURRENT_RUNG;
        const current = index === ORDER_CURRENT_RUNG;
        const last = index === ORDER_RUNGS.length - 1;
        return (
          <Rung key={key}>
            <RungRail>
              <RungDot $lit={done || current}>
                <Icon />
              </RungDot>
              {!last && <RungLine $done={done} />}
            </RungRail>
            <RungTitle $current={current} $todo={!done && !current}>
              {title}
            </RungTitle>
          </Rung>
        );
      })}
    </AppCard>

    <AppCard>
      <CardHead>
        <VerifiedIcon style={{ color: APP.green }} />
        <CardTitle>Paid</CardTitle>
      </CardHead>
      <CardText>
        {deal
          ? `${formatCurrency(deal.price)} received. Thank you.`
          : "Received. Thank you."}
      </CardText>
    </AppCard>

    {deal && (
      <AppCard>
        <CardHead>
          <ReceiptIcon style={{ color: APP.appColor }} />
          <CardTitle>Order</CardTitle>
        </CardHead>
        {deal.vendorName && <CardMeta>Sold by {deal.vendorName}</CardMeta>}
        <OrderLine>
          <span>1x</span>
          <span>{deal.title}</span>
          <span>{formatCurrency(deal.price)}</span>
        </OrderLine>
      </AppCard>
    )}
  </>
);

const OrderSheet = () => (
  <Sheet>
    <SheetButton>Track in My Orders</SheetButton>
    <SheetLink>Continue shopping</SheetLink>
  </Sheet>
);

const DealsScreen = ({ deals }) => (
  <DealGrid>
    {deals.length
      ? deals.map((deal) => (
          <DealCard key={deal.id}>
            <DealImage>
              <img src={deal.image} alt="" loading="lazy" decoding="async" />
            </DealImage>
            <DealTitle>{deal.title}</DealTitle>
            <DealPrice>{formatCurrency(deal.price)}</DealPrice>
            <DealButton>Add to Cart</DealButton>
          </DealCard>
        ))
      : Array.from({ length: 4 }).map((_, index) => (
          <DealCard key={index}>
            <DealImage $skeleton />
            <SkeletonLine $width="80%" $height={12} $gap={10} />
            <SkeletonLine $width="45%" $height={12} $gap={8} />
            <SkeletonLine $width="100%" $height={32} $gap={10} $radius={10} />
          </DealCard>
        ))}
  </DealGrid>
);

/* The two stores, each with a QR code for a visitor on a computer and a
   badge for a visitor already on a phone. The URLs come from config/appStores
   so the badges, the codes and every other store link stay the same. */
const STORES = [
  {
    key: "ios",
    name: "the App Store",
    url: APP_STORE_URL,
    badge: appStoreBadge,
    badgeAlt: "Download on the App Store",
  },
  {
    key: "android",
    name: "Google Play",
    url: PLAY_STORE_URL,
    badge: playStoreBadge,
    badgeAlt: "Get it on Google Play",
  },
];

/* Error correction H (30%) leaves room for the Lusuku mark in the middle,
   which covers about 5% of the code. The mark is the navbar's own file, so
   it is a cache hit. */
const QR_SIZE = 120;
const QR_MARK = {
  src: "/lusuku_logo_main.png",
  width: 28,
  height: 28,
  excavate: true,
};

const FEATURES = [
  {
    Icon: StorefrontOutlinedIcon,
    text: "Restaurants, shops and pharmacies in one app",
  },
  {
    Icon: NotificationsActiveOutlinedIcon,
    text: "Live updates on every order, from the vendor to your door",
  },
  {
    Icon: PaymentsOutlinedIcon,
    text: "Pay with mobile money, card or cash on delivery",
  },
];

const AppDownloadShowcase = ({ restaurants, deals }) => {
  const showcaseRestaurants = showcaseRestaurantsOf(restaurants);
  const showcaseDeals = showcaseDealsOf(deals);

  return (
    <Section>
      <Content>
        <Copy>
          <AppTile>
            {/* The navbar already paints this file, so it is a cache hit. */}
            <img src="/lusuku_logo_main.png" alt="" loading="lazy" />
          </AppTile>
          <Title>Download the App</Title>
          <Subtitle>
            Order anything and track it in real time with the Lusuku app.
          </Subtitle>
          <Features>
            {FEATURES.map(({ Icon, text }) => (
              <Feature key={text}>
                <FeatureIcon>
                  <Icon />
                </FeatureIcon>
                {text}
              </Feature>
            ))}
          </Features>
          <Downloads>
            {STORES.map((store) => (
              <StoreOption key={store.key}>
                <StoreQr>
                  <QRCodeSVG
                    value={store.url}
                    size={QR_SIZE}
                    level="H"
                    marginSize={0}
                    fgColor="#0f172a"
                    bgColor="#ffffff"
                    title={`Scan to get Lusuku on ${store.name}`}
                    imageSettings={QR_MARK}
                  />
                </StoreQr>
                <BadgeLink
                  href={store.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={store.badge} alt={store.badgeAlt} />
                </BadgeLink>
              </StoreOption>
            ))}
          </Downloads>
          <ScanHint>
            Scan a code with your phone camera to get the app.
          </ScanHint>
        </Copy>

        <Stage aria-hidden="true">
          <Phone position="left" title="Restaurants">
            <RestaurantsScreen restaurants={showcaseRestaurants} />
          </Phone>
          <Phone
            position="center"
            title="Thank you for ordering on Lusuku"
            footer={<OrderSheet />}
          >
            <OrderScreen deal={showcaseDeals[0] || null} />
          </Phone>
          <Phone position="right" title="Deals & offers">
            <DealsScreen deals={showcaseDeals} />
          </Phone>
        </Stage>
      </Content>
    </Section>
  );
};

export default AppDownloadShowcase;

/* ── Section and copy ─────────────────────────────────────────────────────── */

const Section = styled.section`
  position: relative;
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  padding: 20px 20px 96px;
  background: linear-gradient(180deg, #ffffff 0%, #f1f5f6 100%);
  border-top: 1px solid #edf1f2;
  /* The tilted side phones reach past the column on narrow screens. */
  overflow: hidden;

  /* On a phone the block above already ends in a 16px white margin, so the
     border would draw a stray line under it and 56px more padding left a
     tall empty band over the logo. The margin and 20px now make the gap. */
  @media (max-width: 768px) {
    padding: 20px 16px 64px;
    border-top: 0;
  }
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr);
  align-items: center;
  gap: 48px;
  max-width: 1120px;
  margin: 0 auto;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 40px;
  }
`;

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 900px) {
    align-items: center;
    text-align: center;
  }
`;

const AppTile = styled.div`
  width: 92px;
  height: 92px;
  border-radius: 22px;
  background: #ffffff;
  display: grid;
  place-items: center;
  margin-bottom: 18px;
  box-shadow:
    0 0 0 1px rgba(15, 23, 42, 0.06),
    0 8px 20px rgba(15, 23, 42, 0.1);

  img {
    width: 80px;
    height: 80px;
    object-fit: contain;
  }
`;

const Title = styled.h2`
  margin: 0 0 12px;
  font-size: 2.4rem;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: #0f172a;
  text-wrap: balance;

  @media (max-width: 768px) {
    font-size: 1.9rem;
  }

  @media (max-width: 480px) {
    font-size: 1.6rem;
  }
`;

const Subtitle = styled.p`
  margin: 0 0 26px;
  max-width: 460px;
  font-size: 1.05rem;
  line-height: 1.55;
  color: #475569;
  text-wrap: balance;

  @media (max-width: 768px) {
    font-size: 0.98rem;
  }
`;

const Features = styled.ul`
  display: grid;
  gap: 14px;
  margin: 0 0 30px;
  padding: 0;
  list-style: none;
  text-align: left;
`;

const Feature = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.98rem;
  line-height: 1.4;
  color: #1e293b;

  @media (max-width: 480px) {
    font-size: 0.92rem;
  }
`;

const FeatureIcon = styled.span`
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #e6eef0;
  color: ${APP.appColor};

  svg {
    font-size: 20px;
  }
`;

/* A phone cannot scan its own screen, so on a phone-sized or touch screen the
   codes and the hint go and the badges stand alone, as before. */
const PHONE_SCREEN = "(max-width: 900px), (hover: none) and (pointer: coarse)";

const Downloads = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;

  @media (max-width: 900px) {
    justify-content: center;
  }
`;

const StoreOption = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 14px 14px 12px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e3e9eb;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);

  @media ${PHONE_SCREEN} {
    padding: 0;
    border: 0;
    background: none;
    box-shadow: none;
  }
`;

const StoreQr = styled.div`
  padding: 6px;
  background: #ffffff;

  svg {
    display: block;
  }

  @media ${PHONE_SCREEN} {
    display: none;
  }
`;

const ScanHint = styled.p`
  margin: 14px 0 0;
  font-size: 0.9rem;
  color: #64748b;

  @media ${PHONE_SCREEN} {
    display: none;
  }
`;

const BadgeLink = styled.a`
  display: block;
  border-radius: 8px;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  img {
    display: block;
    height: 40px;
    width: auto;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.14);
  }

  @media ${PHONE_SCREEN} {
    img {
      height: 44px;
    }
  }

  @media (max-width: 480px) {
    img {
      height: 40px;
    }
  }
`;

/* ── The stage and the phone ──────────────────────────────────────────────── */

/* Every size below this line is in app points; --scale-* turns it into CSS
   pixels. The breakpoints keep the three phones inside the column. */
const Stage = styled.div`
  --scale-center: 0.58;
  --scale-side: 0.5;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 0 28px;
  isolation: isolate;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: min(620px, 118%);
    aspect-ratio: 1;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    background: radial-gradient(
      closest-side,
      rgba(50, 77, 84, 0.16),
      rgba(50, 77, 84, 0.06) 62%,
      rgba(50, 77, 84, 0) 100%
    );
    z-index: -1;
  }

  @media (max-width: 1180px) {
    --scale-center: 0.44;
    --scale-side: 0.38;
  }

  @media (max-width: 900px) {
    --scale-center: 0.52;
    --scale-side: 0.45;
  }

  @media (max-width: 600px) {
    --scale-center: 0.36;
    --scale-side: 0.31;
  }

  @media (max-width: 400px) {
    --scale-center: 0.29;
    --scale-side: 0.25;
  }
`;

const SLOT = {
  left: { tilt: "-7deg", lift: 44, z: 1 },
  center: { tilt: "0deg", lift: 0, z: 3 },
  right: { tilt: "7deg", lift: 44, z: 2 },
};

const PhoneSlot = styled.div`
  --s: var(
    ${({ $position }) =>
      $position === "center" ? "--scale-center" : "--scale-side"}
  );
  position: relative;
  flex: 0 0 auto;
  width: calc(${FRAME_W}px * var(--s));
  height: calc(${FRAME_H}px * var(--s));
  z-index: ${({ $position }) => SLOT[$position].z};
  transform: translateY(
      calc(${({ $position }) => SLOT[$position].lift}px * var(--s))
    )
    rotate(${({ $position }) => SLOT[$position].tilt});
  transition: transform 0.45s cubic-bezier(0.2, 0.8, 0.2, 1);

  ${({ $position }) =>
    $position === "left" &&
    css`
      margin-right: calc(${FRAME_W}px * var(--s) * -0.12);
    `}
  ${({ $position }) =>
    $position === "right" &&
    css`
      margin-left: calc(${FRAME_W}px * var(--s) * -0.12);
    `}

  @media (hover: hover) {
    &:hover {
      z-index: 4;
      transform: translateY(calc(-16px * var(--s))) rotate(0deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const PhoneFrame = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: ${FRAME_W}px;
  height: ${FRAME_H}px;
  padding: ${BEZEL}px;
  box-sizing: border-box;
  border-radius: 58px;
  background: #0b0f14;
  transform: scale(var(--s));
  transform-origin: 0 0;
  box-shadow:
    inset 0 0 0 2px #2b323b,
    0 44px 80px rgba(15, 23, 42, 0.24),
    0 16px 30px rgba(15, 23, 42, 0.14);

  /* Side button and volume rocker, for the silhouette. */
  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 4px;
    border-radius: 2px;
    background: #1f252d;
  }

  &::before {
    right: -4px;
    top: 190px;
    height: 96px;
  }

  &::after {
    left: -4px;
    top: 160px;
    height: 120px;
  }
`;

const Screen = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  border-radius: 48px;
  overflow: hidden;
  isolation: isolate;
  background: ${APP.light};
  color: ${APP.dark};
  font-size: 14px;
  line-height: 1.35;
  text-align: left;
  -webkit-font-smoothing: antialiased;
`;

const Island = styled.div`
  position: absolute;
  top: 11px;
  left: 50%;
  z-index: 6;
  width: 104px;
  height: 31px;
  border-radius: 20px;
  background: #0b0f14;
  transform: translateX(-50%);
`;

const TopBar = styled.div`
  flex-shrink: 0;
  background: ${APP.appColor};
  color: #ffffff;
`;

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: 6px 26px 0 36px;
  font-size: 15px;
  font-weight: 600;

  svg {
    font-size: 17px;
  }
`;

const StatusIcons = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const NavTitle = styled.div`
  height: 50px;
  padding: 0 22px;
  font-size: 17px;
  font-weight: 600;
  line-height: 50px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ScreenBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px 0;
  overflow: hidden;
`;

const HomeIndicator = styled.div`
  position: absolute;
  bottom: 8px;
  left: 50%;
  z-index: 6;
  width: 128px;
  height: 5px;
  border-radius: 3px;
  background: rgba(12, 12, 12, 0.85);
  transform: translateX(-50%);
`;

const SkeletonLine = styled.div`
  width: ${({ $width }) => $width || "100%"};
  height: ${({ $height }) => $height || 12}px;
  margin-top: ${({ $gap }) => $gap || 0}px;
  border-radius: ${({ $radius }) => $radius || 6}px;
  background: #e7ebed;
`;

/* ── Restaurants screen (RestaurantsScreen in the app) ───────────────────── */

const SearchField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  height: 42px;
  padding: 0 14px;
  border-radius: 12px;
  background: #ffffff;
  color: ${APP.medium};
  font-size: 14px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);

  svg {
    font-size: 20px;
  }
`;

const RestaurantCard = styled.div`
  flex-shrink: 0;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.07);
`;

const RestaurantHero = styled.div`
  position: relative;
  display: grid;
  place-items: center;
  aspect-ratio: 1.6;
  border-radius: 20px 20px 0 0;
  overflow: hidden;
  background: ${({ $skeleton }) => ($skeleton ? "#e7ebed" : "#ffffff")};
  color: #c3cbcf;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  img[data-fit="contain"] {
    object-fit: contain;
    padding: 22px 48px;
    box-sizing: border-box;
  }

  > svg {
    font-size: 56px;
  }

  ${({ $dim }) =>
    $dim &&
    css`
      &::after {
        content: "";
        position: absolute;
        inset: 0;
        background: rgba(15, 23, 42, 0.35);
      }
    `}
`;

const PILL_TONES = {
  open: css`
    background: #157a44;
    color: #ffffff;
  `,
  ahead: css`
    background: #f2c811;
    color: #0f172a;
  `,
  closed: css`
    background: rgba(15, 23, 42, 0.78);
    color: #ffffff;
  `,
};

const OpenPill = styled.span`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 2;
  padding: 5px 11px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  ${({ $tone }) => PILL_TONES[$tone]}
`;

const RestaurantBody = styled.div`
  position: relative;
  padding: 13px 16px 15px;
  padding-right: ${({ $withLogo }) => ($withLogo ? 98 : 16)}px;
`;

/* The app's logo tile: a rounded square in the card's own radius, breaking
   the photo's edge, one neutral shadow. Only when the photo is not the logo. */
const LogoTile = styled.div`
  position: absolute;
  top: -38px;
  right: 14px;
  z-index: 3;
  width: 76px;
  height: 76px;
  padding: 2px;
  box-sizing: border-box;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.16);

  img {
    display: block;
    width: 100%;
    height: 100%;
    padding: 4px;
    box-sizing: border-box;
    border: 1px solid rgba(15, 23, 42, 0.12);
    border-radius: 18px;
    background: #ffffff;
    object-fit: contain;
  }
`;

const RestaurantName = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: ${APP.darkGrey};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RestaurantTagline = styled.div`
  margin-top: 3px;
  font-size: 13px;
  line-height: 18px;
  color: ${APP.medium};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CuisineChip = styled.span`
  display: inline-block;
  max-width: 100%;
  margin-top: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  background: #edf2f3;
  color: ${APP.appColor};
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: top;
`;

/* ── Order screen (DoneScreen in the app) ─────────────────────────────────── */

const OrderHero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  padding: 6px 0 4px;
  text-align: center;
`;

const HeroBadge = styled.div`
  display: grid;
  place-items: center;
  width: 64px;
  height: 64px;
  margin-bottom: 12px;
  border-radius: 50%;
  background: ${APP.green};
  color: #ffffff;

  svg {
    font-size: 34px;
  }
`;

const Headline = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${APP.darkGrey};
`;

const NextStep = styled.div`
  margin-top: 6px;
  padding: 0 8px;
  font-size: 14px;
  line-height: 20px;
  color: ${APP.dark};
`;

const LiveRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  font-size: 12px;
  color: ${APP.medium};
`;

const LiveDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${APP.green};
`;

const AppCard = styled.div`
  flex-shrink: 0;
  padding: 16px;
  border-radius: 16px;
  background: #ffffff;
`;

const Rung = styled.div`
  display: flex;
  align-items: flex-start;
`;

const RungRail = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 32px;
`;

const RungDot = styled.div`
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  box-sizing: border-box;
  border-radius: 50%;
  border: 2px solid ${({ $lit }) => ($lit ? APP.stepOrange : APP.hairline)};
  background: ${({ $lit }) => ($lit ? APP.stepOrange : APP.light)};
  color: ${({ $lit }) => ($lit ? "#ffffff" : APP.medium)};

  svg {
    font-size: 16px;
  }
`;

const RungLine = styled.div`
  width: 2px;
  height: 22px;
  margin: 2px 0;
  background: ${({ $done }) => ($done ? APP.stepOrange : APP.hairline)};
`;

const RungTitle = styled.div`
  margin: 5px 0 0 12px;
  font-size: 15px;
  font-weight: ${({ $todo }) => ($todo ? 500 : 600)};
  color: ${({ $current, $todo }) =>
    $current ? APP.stepOrange : $todo ? APP.medium : APP.darkGrey};
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  svg {
    font-size: 20px;
  }
`;

const CardTitle = styled.div`
  flex: 1;
  font-size: 15px;
  font-weight: 700;
  color: ${APP.darkGrey};
`;

const CardText = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: ${APP.dark};
`;

const CardMeta = styled.div`
  margin: -4px 0 8px;
  font-size: 12px;
  color: ${APP.medium};
`;

const OrderLine = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;

  span:nth-child(1) {
    width: 32px;
    flex-shrink: 0;
    font-weight: 600;
    color: ${APP.medium};
  }

  span:nth-child(2) {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span:nth-child(3) {
    margin-left: 8px;
    font-weight: 600;
  }
`;

const Sheet = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  padding: 12px 16px 26px;
  border-radius: 24px 24px 0 0;
  background: #ffffff;
  box-shadow: 0 -6px 12px rgba(0, 0, 0, 0.1);
`;

const SheetButton = styled.div`
  height: 50px;
  border-radius: 25px;
  background: ${APP.appColor};
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  line-height: 50px;
  text-align: center;
`;

const SheetLink = styled.div`
  padding: 12px 0 0;
  font-size: 15px;
  font-weight: 600;
  text-align: center;
  color: ${APP.darkGrey};
`;

/* ── Deals screen ─────────────────────────────────────────────────────────── */

const DealGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  align-content: start;
`;

const DealCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
`;

const DealImage = styled.div`
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  background: ${({ $skeleton }) => ($skeleton ? "#e7ebed" : "#ffffff")};

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const DealTitle = styled.div`
  display: -webkit-box;
  height: 34px;
  margin-top: 8px;
  overflow: hidden;
  font-size: 13px;
  font-weight: 600;
  line-height: 17px;
  color: ${APP.darkGrey};
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const DealPrice = styled.div`
  margin-top: 4px;
  font-size: 14px;
  font-weight: 700;
  color: ${APP.primary};
`;

const DealButton = styled.div`
  height: 32px;
  margin-top: 8px;
  border-radius: 10px;
  background: ${APP.appColor};
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  line-height: 32px;
  text-align: center;
`;
